import type { Project, Track, WamParameterInfo } from '@/domain/types';
import type { EffectNode } from './effects/EffectNode';
import { transportStopped, transportTells } from './wamTransport';
import { compensationFor, type Compensation } from './latency';
import { routingOrder, tempoMapOf, signatureMapOf } from '@/domain/selectors';
import { beatGrid, secondsToTicks, ticksToSeconds } from '@/timing/tempoMap';
import type { AssetManager } from '@/assets/AssetManager';
import { StretchCache, renderSignature, toAudioBuffer } from './stretchCache';
import { LoudnessMeter, type LiveLoudness } from './loudnessMeter';
import { SpectrumAnalyser } from './spectrum';
import { StripLoudness, stripTapOptions } from './stripLoudness';

const METER_PROCESSOR_NAME = 'stage-zero-loudness-tap';
const STRIP_PROCESSOR_NAME = 'stage-zero-strip-tap';

/**
 * The per-strip tap. It weights and reduces in place, posting one number every
 * piece rather than every block; the coefficients arrive from the main thread so
 * the weighting is not defined twice.
 */
const STRIP_PROCESSOR_SOURCE = `
class StripTapProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const o = options.processorOptions;
    this.shelf = o.shelf;
    this.highPass = o.highPass;
    this.pieceLength = o.pieceLength;
    this.states = [];
    this.sum = 0;
    this.count = 0;
    this.peak = 0;
  }
  run(filter, state, x0) {
    const y0 = filter.b0 * x0 + filter.b1 * state.x1 + filter.b2 * state.x2
      - filter.a1 * state.y1 - filter.a2 * state.y2;
    state.x2 = state.x1; state.x1 = x0; state.y2 = state.y1; state.y1 = y0;
    return y0;
  }
  process(inputs) {
    const input = inputs[0];
    if (!input || input.length === 0 || !input[0]) return true;
    while (this.states.length < input.length) {
      this.states.push([{ x1: 0, x2: 0, y1: 0, y2: 0 }, { x1: 0, x2: 0, y1: 0, y2: 0 }]);
    }
    const frames = input[0].length;
    for (let i = 0; i < frames; i++) {
      for (let c = 0; c < input.length; c++) {
        const raw = input[c][i];
        const magnitude = raw < 0 ? -raw : raw;
        if (magnitude > this.peak) this.peak = magnitude;
        const value = this.run(this.highPass, this.states[c][1], this.run(this.shelf, this.states[c][0], raw));
        this.sum += value * value;
      }
      this.count++;
      if (this.count >= this.pieceLength) {
        this.port.postMessage({ meanSquare: this.sum / this.count, peak: this.peak });
        this.sum = 0;
        this.count = 0;
      }
    }
    return true;
  }
}
registerProcessor('${STRIP_PROCESSOR_NAME}', StripTapProcessor);
`;

/**
 * The loudness tap. It copies whatever it is given to the main thread and
 * produces no output of its own; all the measurement happens in `LoudnessMeter`,
 * where it can be tested.
 */
const METER_PROCESSOR_SOURCE = `
class LoudnessTapProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0];
    if (input && input.length > 0 && input[0] && input[0].length > 0) {
      const channels = [];
      for (let c = 0; c < input.length; c++) channels.push(new Float32Array(input[c]));
      this.port.postMessage(channels, channels.map((c) => c.buffer));
    }
    return true;
  }
}
registerProcessor('${METER_PROCESSOR_NAME}', LoudnessTapProcessor);
`;
import { SchedulerPump, Timeline } from './clock';
import { TrackGraph, type MeterReading } from './TrackGraph';
import { fadeGainAt, planWindow, type VoiceFade, type VoicePlan } from './schedulePlan';
import { applyVoiceEnvelope } from './voiceEnvelope';
import { planMidiWindow, type NotePlan } from './midiPlan';
import { readSamplerZones } from './instruments/samplerState';
import { WorkletModule } from './workletModule';
import type { InstrumentNode } from './instruments';
import {
  activeLanes,
  applyLaneSnapshot,
  automatedTrackParams,
  resolveParamDescriptor,
  writeLaneAutomation,
} from './automation';

/**
 * The audio engine.
 *
 * Responsibilities, in order of importance:
 *
 *  1. Keep a correct audio graph that mirrors the project without rebuilding
 *     itself for changes that do not alter routing.
 *  2. Schedule clips ahead of the audio clock so that no sound is ever
 *     "triggered now" from the main thread.
 *  3. Report position and metering back to the UI without becoming the UI's
 *     render loop.
 *
 * Nothing here reads React state, and nothing in React writes to it directly:
 * the store calls `applyProject` after every change, and the engine reconciles.
 */

export type WamInfoListener = (info: { trackId: string; effectId: string | null; name: string; parameters: WamParameterInfo[] }) => void;

export interface LatencyReport {
  /** How late everything arrives now, because of the longest route through a plugin. */
  pluginLatencySec: number;
  /** How far each track's output is held back to line up, by track; tracks held back by nothing are absent. */
  heldBackSec: Map<string, number>;
}
export type LatencyListener = (report: LatencyReport) => void;

/** What a compensation says to a person: the total, and each track's hold on its output route. */
export function latencyReport(delays: Compensation): LatencyReport {
  const heldBackSec = new Map<string, number>();
  for (const [trackId, shared] of delays.common) heldBackSec.set(trackId, shared + (delays.output.get(trackId) ?? 0));
  for (const [trackId, extra] of delays.output) if (!delays.common.has(trackId)) heldBackSec.set(trackId, extra);
  return { pluginLatencySec: delays.longestSec, heldBackSec };
}

export type TransportState = 'stopped' | 'playing' | 'recording';

export interface EngineStatus {
  contextState: AudioContextState | 'uninitialised';
  sampleRate: number;
  baseLatencySec: number;
  outputLatencySec: number;
  voiceCount: number;
}

const LOOKAHEAD_SEC = 0.25;
/** How far ahead automation is written when playback has no defined end. */
const AUTOMATION_HORIZON_SEC = 60 * 30;
const PUMP_INTERVAL_MS = 25;
const START_LEAD_SEC = 0.08;
const STOP_FADE_SEC = 0.008;

interface Voice {
  source: AudioBufferSourceNode;
  gain: GainNode;
  /** The clip's own gain envelope, when it has one. Null costs nothing. */
  envelope: GainNode | null;
  assetId: string;
  endsAtContextTime: number;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private assets: AssetManager | null = null;
  /** Pitch-preserved renders, shared with the offline renderer through services. */
  private stretches: StretchCache = new StretchCache(async () => null);
  /** What `stretches` was last prepared for; null when it has not been. */
  private preparedSignature: string | null = null;
  /** The preparation run in flight, so an edit can supersede it. */
  private preparing: AbortController | null = null;
  /** Those renders wrapped in buffers belonging to this context. */
  private readonly stretchBuffers = new Map<string, AudioBuffer>();
  private meter: LoudnessMeter | null = null;
  private readonly strips = new Map<string, StripLoudness>();
  private readonly stripNodes = new Map<string, AudioWorkletNode>();
  private readonly stripPending = new Set<string>();
  private readonly stripModule = new WorkletModule(STRIP_PROCESSOR_SOURCE);
  private spectrum: SpectrumAnalyser | null = null;
  private meterNode: AudioWorkletNode | null = null;
  private readonly meterModule = new WorkletModule(METER_PROCESSOR_SOURCE);
  private meterPending = false;
  private project: Project | null = null;

  private graphs = new Map<string, TrackGraph>();
  private connectedTo = new Map<string, AudioNode>();
  private voices = new Set<Voice>();
  private readonly timeline = new Timeline();
  private readonly pump = new SchedulerPump(PUMP_INTERVAL_MS);

  private state: TransportState = 'stopped';
  private positionTicks = 0;
  private scheduledUntilSec = 0;
  private metronomeScheduledUntilSec = 0;
  private clickBuffer: AudioBuffer | null = null;
  private metronomeGain: GainNode | null = null;
  private pendingAssets = new Set<string>();
  private latency: Compensation | null = null;
  private latencyListeners = new Set<LatencyListener>();
  private lastLatencyReport = '';
  /**
   * Assets asked for by `warmInstrumentAssets` that had no audio behind them,
   * and the asset record they were asked against. A file that is missing is
   * not asked for again until the project's assets change — asking again on
   * every apply would have two missing zones re-requesting each other for ever.
   */
  private missingAssets = new Set<string>();
  private missingAssetsFor: Project['assets'] | null = null;
  private stateListeners = new Set<(state: TransportState) => void>();
  private wamInfoListeners = new Set<WamInfoListener>();
  private lastAutomationRef: Project['automation'] | null = null;

  // -- lifecycle -----------------------------------------------------------

  /**
   * Creates the AudioContext. Browsers require a user gesture before audio can
   * start, so this is called from the first click rather than at import time —
   * calling it early produces a suspended context and silent playback that is
   * very hard for a user to diagnose.
   */
  /**
   * Gives the engine the asset pipeline, before there is any audio.
   *
   * Decoding a file and rendering a stretch need *a* `BaseAudioContext`, not the
   * live one — an `OfflineAudioContext` will do, and needs no user gesture. So
   * the engine can prepare everything a project needs while the browser is still
   * waiting for its first click, which is the difference between pressing play
   * and hearing the project, and pressing play and hearing the clips that
   * happened to be ready.
   *
   * Separate from `initialise` because the two now happen at different times and
   * for different reasons: this at load, that at the first gesture.
   */
  useAssets(assets: AssetManager): void {
    this.assets = assets;
    // The renders survive the session because the same storage that holds the
    // audio holds them; see docs/RENDER_CACHE.md.
    this.stretches = new StretchCache((assetId) => this.assets?.getBuffer(assetId) ?? Promise.resolve(null), assets.storage);
    // A new cache holds nothing, so nothing has been prepared into it.
    this.preparedSignature = null;
  }

  async initialise(): Promise<AudioContext> {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') await this.ctx.resume();
      return this.ctx;
    }

    const Ctor =
      (globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ??
      (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) throw new Error('This browser does not support the Web Audio API, so Stage Zero cannot play audio.');

    const ctx = new Ctor({ latencyHint: 'interactive' });
    this.ctx = ctx;

    this.metronomeGain = ctx.createGain();
    this.metronomeGain.gain.value = 0.35;
    this.metronomeGain.connect(ctx.destination);
    this.clickBuffer = buildClick(ctx);

    if (ctx.state === 'suspended') await ctx.resume();
    if (this.project) this.applyProject(this.project);
    return ctx;
  }

  get isInitialised(): boolean {
    return this.ctx !== null;
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  get assetManager(): AssetManager | null {
    return this.assets;
  }

  /**
   * Attaches the loudness tap to a node, once.
   *
   * The worklet returns silence from its own output and is left unconnected, so
   * it is a listener rather than a link in the chain: if it fails to load, or
   * the browser has no worklet support, the mix is unaffected and the meter
   * simply reads nothing.
   */
  private async attachMeter(ctx: AudioContext, source: AudioNode): Promise<void> {
    if (this.meterNode || this.meterPending || !ctx.audioWorklet) return;
    this.meterPending = true;
    try {
      await this.meterModule.ensure(ctx);
      const node = new AudioWorkletNode(ctx, METER_PROCESSOR_NAME, {
        numberOfInputs: 1,
        numberOfOutputs: 0,
      });
      this.meter = new LoudnessMeter(ctx.sampleRate);
      this.spectrum = new SpectrumAnalyser(ctx.sampleRate);
      node.port.onmessage = (event: MessageEvent<Float32Array[]>) => {
        this.meter?.push(event.data);
        this.spectrum?.push(event.data);
      };
      source.connect(node);
      this.meterNode = node;
    } catch {
      this.meterNode = null;
      this.meter = null;
    } finally {
      this.meterPending = false;
    }
  }

  /**
   * Attaches a loudness tap to one channel strip.
   *
   * Unlike the master tap, this one does its own arithmetic: the worklet
   * K-weights the block and posts a single mean square every hundred
   * milliseconds. Posting raw audio for every track would be four hundred
   * messages a second each, which on a large desk is more traffic than the
   * measurement is worth. The filter coefficients come from the same function
   * the offline measurement uses, so there is still one definition of what
   * K-weighting is.
   */
  private async attachStripTap(ctx: AudioContext, trackId: string, source: AudioNode): Promise<void> {
    if (this.stripNodes.has(trackId) || this.stripPending.has(trackId) || !ctx.audioWorklet) return;
    this.stripPending.add(trackId);
    try {
      // Every strip asks at once when a project is first applied; the module
      // shares one load between them rather than registering the processor N
      // times. See `WorkletModule`.
      await this.stripModule.ensure(ctx);
      const node = new AudioWorkletNode(ctx, STRIP_PROCESSOR_NAME, {
        numberOfInputs: 1,
        numberOfOutputs: 0,
        processorOptions: stripTapOptions(ctx.sampleRate),
      });
      const strip = new StripLoudness();
      node.port.onmessage = (event: MessageEvent<{ meanSquare: number; peak: number }>) => {
        strip.push(event.data.meanSquare, event.data.peak);
      };
      source.connect(node);
      this.stripNodes.set(trackId, node);
      this.strips.set(trackId, strip);
    } catch {
      this.stripNodes.delete(trackId);
    } finally {
      this.stripPending.delete(trackId);
    }
  }

  /** Momentary loudness of every track that has a tap, in LUFS. */
  readStripLoudness(): Map<string, number> {
    const out = new Map<string, number>();
    for (const [trackId, strip] of this.strips) out.set(trackId, strip.momentaryLufs());
    return out;
  }

  /**
   * What the master bus currently measures, or null when nothing is running.
   *
   * The tap is a worklet on the master output. An AnalyserNode would have been
   * simpler, but it hands out whatever happens to be in its buffer when it is
   * asked — so consecutive reads overlap or skip, and a loudness figure built
   * from them measures the polling rate as much as the audio. A worklet sees
   * every sample exactly once, which is the only way an integrated figure means
   * anything.
   */
  readLoudness(): LiveLoudness | null {
    return this.meter ? this.meter.read() : null;
  }

  /** Starts the loudness measurement again from nothing. */
  resetLoudness(): void {
    this.meter?.reset();
    this.spectrum?.reset();
    for (const strip of this.strips.values()) strip.reset();
  }

  /**
   * The master spectrum, in decibels per band, or null when nothing is running.
   *
   * The transform happens here rather than as audio arrives, so a closed panel
   * costs nothing: samples are only being copied into a ring buffer until
   * somebody asks to see them.
   */
  readSpectrum(): { levels: Float32Array; frequencies: Float32Array } | null {
    if (!this.spectrum) return null;
    return { levels: this.spectrum.read(), frequencies: this.spectrum.bandFrequencies() };
  }

  /** The stretch cache, so a render or a command can prepare it before playing. */
  get stretchCache(): StretchCache {
    return this.stretches;
  }

  /**
   * What the cache was last prepared for, or null if it has not been.
   *
   * Public because it is the honest answer to "is this project ready to play",
   * and because it is what a test can ask.
   */
  get preparedFor(): string | null {
    return this.preparedSignature;
  }

  /**
   * Renders every stretch the project needs and forgets the ones it does not.
   *
   * Returns whether it did any work. False means either that there was nothing
   * to do or that nothing *could* be done yet, and the difference matters to
   * nobody: in both cases the caller has no reason to say it is busy.
   *
   * **The bookkeeping lives here rather than in the store, and that is the
   * point.** It used to live there, which meant the store recorded a project as
   * prepared while the audio context did not yet exist — so every render
   * silently resolved to nothing, the signature was remembered as done, and
   * starting audio never went back for the work. A project opened from disk then
   * discovered every stretch mid-bar, which is exactly what preparing ahead is
   * supposed to prevent. Only the engine knows whether it can render, so only
   * the engine can know whether it has.
   */
  async prepareStretches(
    project: Project,
    onProgress?: (done: number, total: number) => void,
  ): Promise<boolean> {
    if (!this.assets) {
      // Nothing to render from. Forgetting what was prepared is what makes the
      // next call — once there is an asset pipeline — do the work.
      //
      // Note that the *live* context is not required: rendering is arithmetic
      // over decoded samples, and decoding needs only some context. Requiring
      // one here would put every stretch behind the user's first click for no
      // reason at all.
      this.preparedSignature = null;
      return false;
    }

    const signature = renderSignature(project);
    if (signature === this.preparedSignature) return false;
    this.preparedSignature = signature;

    // An edit supersedes the run in flight rather than queueing behind it.
    this.preparing?.abort();
    const controller = new AbortController();
    this.preparing = controller;

    try {
      await this.stretches.prepare(project, { signal: controller.signal, onProgress });
    } finally {
      if (this.preparing === controller) this.preparing = null;
    }

    this.stretches.evictUnused(project);
    for (const key of [...this.stretchBuffers.keys()]) {
      if (!this.stretches.hasKey(key)) this.stretchBuffers.delete(key);
    }
    return signature !== '';
  }

  get transportState(): TransportState {
    return this.state;
  }

  onTransportStateChange(listener: (state: TransportState) => void): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  private setState(state: TransportState): void {
    if (this.state === state) return;
    this.state = state;
    for (const listener of this.stateListeners) listener(state);
  }

  status(): EngineStatus {
    return {
      contextState: this.ctx?.state ?? 'uninitialised',
      sampleRate: this.ctx?.sampleRate ?? 0,
      baseLatencySec: this.ctx?.baseLatency ?? 0,
      outputLatencySec: this.ctx?.outputLatency ?? 0,
      voiceCount: this.voices.size,
    };
  }

  // -- graph reconciliation ------------------------------------------------

  /**
   * Brings the audio graph in line with a project. Called after every command.
   * The work is proportional to what changed, not to the size of the project:
   * tracks that are reference-equal to their previous version still get their
   * cheap parameter update (a few `setTargetAtTime` calls) but never a rebuild.
   */
  applyProject(project: Project): void {
    this.project = project;
    this.warmInstrumentAssets(project);
    const ctx = this.ctx;
    if (!ctx) return;

    // Remove graphs for tracks that no longer exist.
    for (const [trackId, graph] of this.graphs) {
      if (!project.tracks[trackId]) {
        graph.dispose();
        this.graphs.delete(trackId);
        this.connectedTo.delete(trackId);
      }
    }

    // Create graphs for new tracks.
    for (const trackId of Object.keys(project.tracks)) {
      if (!this.graphs.has(trackId)) {
        this.graphs.set(
          trackId,
          new TrackGraph(
            ctx,
            trackId,
            (assetId) => this.assets?.peekBuffer(assetId) ?? null,
            (effectId, info) => {
              for (const listener of this.wamInfoListeners) listener({ trackId, effectId, ...info });
            },
          ),
        );
      }
    }

    // Patch outputs in routing order so a bus exists before its sources connect.
    for (const trackId of routingOrder(project)) {
      const track = project.tracks[trackId];
      const graph = this.graphs.get(trackId);
      if (!track || !graph) continue;

      const destination =
        track.kind === 'master'
          ? ctx.destination
          : (this.graphs.get(track.outputTrackId ?? '')?.input ?? ctx.destination);

      // Re-patching is avoided unless the destination genuinely changed: a
      // disconnect/reconnect during playback drops a buffer's worth of audio.
      if (this.connectedTo.get(trackId) !== destination) {
        graph.connectOutput(destination);
        this.connectedTo.set(trackId, destination);
      }

      graph.update(project, track, automatedTrackParams(project, trackId));
      this.applySends(track, graph);
      // An insert still loading its worklet has no parameter for its lanes to
      // reach yet; when it lands, the lanes are written again so they do.
      const loading = graph.whenReady();
      if (loading) {
        void loading.then(() => {
          if (this.project !== project) return;
          // A plugin that has just landed may have brought a latency with it.
          this.alignLatencies(project);
          if (this.state !== 'stopped') this.rescheduleAutomation();
        });
      }

      // The loudness tap hangs off the master, in parallel with the output, so
      // it sees the finished mix and cannot affect it.
      if (track.kind === 'master') void this.attachMeter(ctx, graph.output);
      void this.attachStripTap(ctx, trackId, graph.output);
    }

    // Routing or chains may have changed; line the paths up again.
    this.alignLatencies(project);

    // An automation edit while the transport is rolling has to be rescheduled:
    // the events already on the audio thread describe the old curve. Reference
    // equality makes this free for every edit that is not an automation edit.
    const automationChanged = this.lastAutomationRef !== project.automation;
    this.lastAutomationRef = project.automation;
    if (automationChanged) {
      if (this.state !== 'stopped') this.rescheduleAutomation();
      else this.snapshotAutomation(this.positionTicks);
    }
  }

  private applySends(track: Track, graph: TrackGraph): void {
    const wanted = new Set(track.sends.map((s) => s.id));
    for (const sendId of graph.sendIds()) {
      if (!wanted.has(sendId)) graph.removeSend(sendId);
    }
    for (const send of track.sends) {
      const target = this.graphs.get(send.targetTrackId);
      if (!target) continue;
      const { gain, out } = graph.ensureSend(send.id, send.preFader);
      out.disconnect();
      out.connect(target.input);
      const ctx = this.ctx;
      if (ctx) gain.gain.setTargetAtTime(send.enabled ? send.gain : 0, ctx.currentTime, 0.02);
    }
  }

  /**
   * Starts decoding the audio a project's instruments and effects hold, and
   * applies the project again as each arrives.
   *
   * A clip's audio is decoded when the scheduler first reaches it. A sampler's
   * zones and a reverb's room are not scheduled — a zone plays when a key is
   * pressed, a room the moment the graph is built — so a project opened from
   * disk would hand them buffers that were never asked for: a zone silent
   * until something else happened to decode its file, a reverb on its
   * synthesised tail. They are asked for here, at the moment the project is
   * known, which needs no audio context and so happens before the first click
   * as well as after it.
   *
   * An asset with no audio behind it — a file gone missing — is remembered as
   * such and not asked for again until the project's assets change. Asking on
   * every apply looked harmless and was not: with two missing zones, each one
   * resolving applied the project and re-asked for the other, for ever.
   */
  private warmInstrumentAssets(project: Project): void {
    const assets = this.assets;
    if (!assets) return;
    if (this.missingAssetsFor !== project.assets) {
      this.missingAssets.clear();
      this.missingAssetsFor = project.assets;
    }
    const wanted = new Set<string>();
    for (const track of Object.values(project.tracks)) {
      for (const effect of track.effects) if (effect.impulseAssetId) wanted.add(effect.impulseAssetId);
      if (track.instrument?.type === 'sampler') {
        for (const zone of readSamplerZones(track.instrument.data)) wanted.add(zone.assetId);
      }
    }
    for (const assetId of wanted) {
      if (assets.peekBuffer(assetId) || this.pendingAssets.has(assetId) || this.missingAssets.has(assetId)) continue;
      this.pendingAssets.add(assetId);
      void assets
        .getBuffer(assetId)
        .then((buffer) => {
          if (!buffer) {
            this.missingAssets.add(assetId);
            return;
          }
          if (this.project) this.applyProject(this.project);
        })
        .catch(() => this.missingAssets.add(assetId))
        .finally(() => this.pendingAssets.delete(assetId));
    }
  }

  /** Told what a hosted plugin said about itself once it loaded on the live graph. */
  onWamInfo(listener: WamInfoListener): () => void {
    this.wamInfoListeners.add(listener);
    return () => this.wamInfoListeners.delete(listener);
  }

  /**
   * Told whenever the routes are lined up again: how far each track's output
   * is held back, and how late everything now arrives — what the strip and
   * the status bar show.
   */
  onLatencyChange(listener: LatencyListener): () => void {
    this.latencyListeners.add(listener);
    return () => this.latencyListeners.delete(listener);
  }

  /**
   * Every hosted plugin's state, as the plugins report it now: what a save
   * writes into the project, since a plugin's interface changes its state
   * without a command. A plugin that is not loaded reports nothing and keeps
   * whatever the project already had.
   */
  async captureWamStates(): Promise<{ trackId: string; effectId: string | null; state: unknown }[]> {
    const out: { trackId: string; effectId: string | null; state: unknown }[] = [];
    for (const [trackId, graph] of this.graphs) {
      for (const { effectId, node } of graph.hostedPlugins()) {
        const state = await node.captureState?.();
        if (state !== null && state !== undefined) out.push({ trackId, effectId, state });
      }
    }
    return out;
  }

  /** The live node for an insert, for a hosted plugin's own interface. */
  effectNode(trackId: string, effectId: string): EffectNode | null {
    return this.graphs.get(trackId)?.effectNode(effectId) ?? null;
  }

  /** The instrument on a track, for live play-through from a MIDI device. */
  instrumentFor(trackId: string): InstrumentNode | null {
    return this.graphs.get(trackId)?.instrumentNode ?? null;
  }

  /** The input node for a track, used by the recorder for monitoring. */
  trackInput(trackId: string): GainNode | null {
    return this.graphs.get(trackId)?.input ?? null;
  }

  readMeters(): Map<string, MeterReading> {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const out = new Map<string, MeterReading>();
    for (const [trackId, graph] of this.graphs) out.set(trackId, graph.readMeter(now));
    return out;
  }

  // -- transport -----------------------------------------------------------

  /** Current playhead, in ticks, derived from the audio clock while playing. */
  currentTicks(): number {
    const ctx = this.ctx;
    const project = this.project;
    if (!ctx || !project || this.state === 'stopped' || this.timeline.isEmpty) return this.positionTicks;
    const seconds = this.timeline.projectSecondsAt(ctx.currentTime);
    return secondsToTicks(tempoMapOf(project), seconds);
  }

  currentSeconds(): number {
    const ctx = this.ctx;
    const project = this.project;
    if (!project) return 0;
    if (!ctx || this.state === 'stopped' || this.timeline.isEmpty) {
      return ticksToSeconds(tempoMapOf(project), this.positionTicks);
    }
    return this.timeline.projectSecondsAt(ctx.currentTime);
  }

  seek(ticks: number): void {
    const wasPlaying = this.state === 'playing';
    this.positionTicks = Math.max(0, ticks);
    if (wasPlaying) {
      this.stopVoices(true);
      this.beginPlayback(this.positionTicks);
    } else {
      this.snapshotAutomation(this.positionTicks);
    }
  }

  play(fromTicks?: number): void {
    const ctx = this.ctx;
    const project = this.project;
    if (!ctx || !project) return;
    if (this.state === 'playing') return;
    void ctx.resume();
    this.beginPlayback(fromTicks ?? this.positionTicks);
    this.setState('playing');
  }

  pause(): void {
    if (this.state === 'stopped') return;
    this.positionTicks = this.currentTicks();
    this.haltPlayback();
    this.snapshotAutomation(this.positionTicks);
    this.setState('stopped');
  }

  stop(): void {
    this.haltPlayback();
    this.snapshotAutomation(this.positionTicks);
    this.setState('stopped');
  }

  /** Stops sound but keeps the position, used when recording ends. */
  private haltPlayback(): void {
    this.pump.stop();
    this.stopVoices(false);
    this.timeline.clear();
    this.tellTransportStopped();
  }

  private beginPlayback(fromTicks: number): void {
    const ctx = this.ctx;
    const project = this.project;
    if (!ctx || !project) return;

    const map = tempoMapOf(project);
    const startSec = ticksToSeconds(map, Math.max(0, fromTicks));
    const loopStartSec = ticksToSeconds(map, project.transport.loopStartTicks);
    const loopEndSec = ticksToSeconds(map, project.transport.loopEndTicks);
    const looping = project.transport.loopEnabled && loopEndSec > loopStartSec;

    // Starting inside the loop keeps the loop end; starting outside it plays
    // straight through until the loop is entered, which is what users expect
    // when they click ahead of the loop region.
    const segmentEnd = looping && startSec >= loopStartSec && startSec < loopEndSec ? loopEndSec : Infinity;

    const contextStart = ctx.currentTime + START_LEAD_SEC;
    this.timeline.reset({
      contextTime: contextStart,
      projectSeconds: startSec,
      endProjectSeconds: segmentEnd,
    });
    this.scheduledUntilSec = startSec;
    this.metronomeScheduledUntilSec = startSec;
    this.scheduleAutomationForSegment(project, startSec, segmentEnd);
    this.tellTransportForSegment(project, startSec, segmentEnd);
    this.pump.start(() => this.tick());
    this.tick();
  }

  /**
   * Delays every leaf track's output to the longest path, so a track through
   * a late-answering plugin arrives with the rest. Set on the graphs from the
   * project's routing and what each chain reports; a chain still loading its
   * plugin reports nothing yet and is aligned again when it lands.
   */
  private alignLatencies(project: Project): void {
    const delays = compensationFor(project, (trackId) => this.graphs.get(trackId)?.chainLatencySec() ?? 0);
    for (const [trackId, graph] of this.graphs) {
      graph.setCompensation(delays.common.get(trackId) ?? 0, delays.output.get(trackId) ?? 0, delays.sends.get(trackId) ?? new Map());
    }
    this.latency = delays;
    const report = latencyReport(delays);
    // Only a change is worth telling: this runs on every project change.
    const key = JSON.stringify([report.pluginLatencySec, [...report.heldBackSec]]);
    if (key === this.lastLatencyReport) return;
    this.lastLatencyReport = key;
    for (const listener of this.latencyListeners) listener(report);
  }

  /** How the routes are held back right now, and how late everything therefore arrives. */
  get compensation(): Compensation | null {
    return this.latency;
  }

  /**
   * Tells every hosted plugin the transport across a segment: where it is,
   * how fast, and that it is playing — again at every bar line and tempo or
   * meter event, since a plugin works its position out from the last tell.
   */
  private tellTransportForSegment(project: Project, fromSeconds: number, toSeconds: number): void {
    const timeFor = (seconds: number): number | null => this.timeline.contextTimeFor(seconds);
    const tells = transportTells(project, fromSeconds, toSeconds, timeFor);
    if (tells.length === 0) return;
    for (const graph of this.graphs.values()) graph.tellTransport(tells);
  }

  /** Tells every hosted plugin the transport has stopped, now. */
  private tellTransportStopped(): void {
    const ctx = this.ctx;
    const project = this.project;
    if (!ctx || !project) return;
    const tell = transportStopped(project, this.positionTicks, ctx.currentTime);
    for (const graph of this.graphs.values()) graph.tellTransport([tell]);
  }

  // -- scheduling ----------------------------------------------------------

  /**
   * Writes every active lane across the segment that is currently playing.
   *
   * Automation is scheduled per segment, not per look-ahead window: a lane's
   * events are few, and re-anchoring a long ramp every 25ms would turn a smooth
   * eight-bar fade into a staircase.
   */
  private scheduleAutomationForSegment(project: Project, fromSeconds: number, toSeconds: number): void {
    const ctx = this.ctx;
    if (!ctx) return;

    const map = tempoMapOf(project);
    const fromTick = secondsToTicks(map, fromSeconds);
    // A non-looping pass has no end; schedule to the end of the material plus
    // a generous margin so a tail-out fade still lands.
    const end = Number.isFinite(toSeconds) ? toSeconds : fromSeconds + AUTOMATION_HORIZON_SEC;
    const toTick = secondsToTicks(map, Math.max(end, fromSeconds));
    const timeFor = (seconds: number): number | null => this.timeline.contextTimeFor(seconds);

    for (const lane of activeLanes(project)) {
      const descriptor = resolveParamDescriptor(project, lane.target);
      const graph = this.graphs.get(lane.target.trackId);
      if (!descriptor || !graph) continue;
      const targets = graph.automationTargets(lane.target);
      writeLaneAutomation(targets, lane, descriptor, fromTick, toTick, map, timeFor);
    }
  }

  /** Re-writes automation from now to the end of the current segment. */
  private rescheduleAutomation(): void {
    const ctx = this.ctx;
    const project = this.project;
    if (!ctx || !project || this.timeline.isEmpty) return;
    const anchor = this.timeline.anchorAt(ctx.currentTime);
    if (!anchor) return;
    const nowSeconds = this.timeline.projectSecondsAt(ctx.currentTime);
    this.scheduleAutomationForSegment(project, nowSeconds, anchor.endProjectSeconds);
  }

  /**
   * Parks every automated parameter at the value its lane holds at `tick`.
   * Called when the transport stops or the playhead moves while stopped, so a
   * parameter stays where the automation put it instead of springing back to
   * the fader position.
   */
  private snapshotAutomation(tick: number): void {
    const ctx = this.ctx;
    const project = this.project;
    if (!ctx || !project) return;
    for (const lane of activeLanes(project)) {
      const descriptor = resolveParamDescriptor(project, lane.target);
      const graph = this.graphs.get(lane.target.trackId);
      if (!descriptor || !graph) continue;
      const targets = graph.automationTargets(lane.target);
      applyLaneSnapshot(targets, lane, descriptor, tick, ctx.currentTime);
    }
  }

  private tick(): void {
    const ctx = this.ctx;
    const project = this.project;
    if (!ctx || !project || this.state === 'stopped') return;

    const horizon = ctx.currentTime + LOOKAHEAD_SEC;
    const map = tempoMapOf(project);
    const loopStartSec = ticksToSeconds(map, project.transport.loopStartTicks);
    const loopEndSec = ticksToSeconds(map, project.transport.loopEndTicks);
    const looping = project.transport.loopEnabled && loopEndSec > loopStartSec;

    // Fill the look-ahead window, crossing loop boundaries as needed. The guard
    // bounds an otherwise unbounded loop if a project ever produced a zero-length
    // segment; it can only be hit by corrupt data, never by normal use.
    for (let guard = 0; guard < 64; guard++) {
      const anchor = this.timeline.anchorAt(horizon);
      if (!anchor) break;

      const windowEnd = Math.min(
        anchor.endProjectSeconds,
        anchor.projectSeconds + (horizon - anchor.contextTime),
      );
      if (windowEnd > this.scheduledUntilSec) {
        this.scheduleWindow(project, anchor.projectSeconds, anchor.endProjectSeconds, this.scheduledUntilSec, windowEnd);
        this.scheduleMetronome(project, this.scheduledUntilSec, windowEnd);
        this.scheduledUntilSec = windowEnd;
      }

      if (looping && this.scheduledUntilSec >= anchor.endProjectSeconds && Number.isFinite(anchor.endProjectSeconds)) {
        const wrapContextTime = anchor.contextTime + (anchor.endProjectSeconds - anchor.projectSeconds);
        this.timeline.push({
          contextTime: wrapContextTime,
          projectSeconds: loopStartSec,
          endProjectSeconds: loopEndSec,
        });
        this.scheduledUntilSec = loopStartSec;
        this.metronomeScheduledUntilSec = loopStartSec;
        // Each pass round the loop re-writes automation for the new segment, so
        // a curve inside the loop repeats exactly rather than holding its final
        // value from the previous lap — and tells the plugins the bar count
        // has gone back to the loop's start.
        this.scheduleAutomationForSegment(project, loopStartSec, loopEndSec);
        this.tellTransportForSegment(project, loopStartSec, loopEndSec);
        continue;
      }
      break;
    }

    this.timeline.prune(ctx.currentTime - 1);
    this.reapVoices(ctx.currentTime);
  }

  private scheduleWindow(
    project: Project,
    segmentStartSec: number,
    segmentEndSec: number,
    windowStartSec: number,
    windowEndSec: number,
  ): void {
    const bounds = { segmentStartSec, segmentEndSec, windowStartSec, windowEndSec };
    for (const plan of planWindow(project, bounds)) this.startVoice(plan);
    for (const note of planMidiWindow(project, bounds)) this.startNote(note);
  }

  /**
   * Hands one planned note to its track's instrument at an exact context time.
   * Like audio voices, notes are scheduled ahead of the clock, never triggered
   * "now" — the instrument is told when, and the audio thread does the rest.
   */
  private startNote(plan: NotePlan): void {
    const instrument = this.graphs.get(plan.trackId)?.instrumentNode;
    if (!instrument) return;
    const when = this.timeline.contextTimeFor(plan.startProjectSec);
    if (when === null) return;

    const ctx = this.ctx;
    if (!ctx) return;
    // A note whose start has already slipped past begins immediately, shortened
    // by however late it is, rather than being dropped.
    const lateBy = Math.max(0, ctx.currentTime - when);
    const duration = plan.durationSec - lateBy;
    if (duration <= 0) return;
    instrument.playNote(plan.pitch, plan.velocity, Math.max(when, ctx.currentTime), duration);
  }

  /**
   * The buffer a voice should read, decoding or stretching it in the background
   * if it is not ready.
   *
   * Both misses are handled the same way: drop this voice and start the work, so
   * the next pass over the same material plays. Blocking the scheduler on I/O or
   * on a stretch would stall every other track too, which is a far worse
   * artefact than one clip arriving a beat late the first time it is reached.
   */
  private resolveBuffer(plan: VoicePlan, ctx: AudioContext, assets: AssetManager): AudioBuffer | null {
    const decoded = assets.peekBuffer(plan.assetId);
    if (!decoded) {
      if (!this.pendingAssets.has(plan.assetId)) {
        this.pendingAssets.add(plan.assetId);
        void assets.getBuffer(plan.assetId).finally(() => this.pendingAssets.delete(plan.assetId));
      }
      return null;
    }
    if (plan.bufferKey === plan.assetId) return decoded;

    const ready = this.stretchBuffers.get(plan.bufferKey);
    if (ready) return ready;

    const audio = this.stretches.getByKey(plan.bufferKey);
    if (audio) {
      const wrapped = toAudioBuffer(ctx, audio);
      this.stretchBuffers.set(plan.bufferKey, wrapped);
      return wrapped;
    }
    // Not rendered yet. Preparation is kicked off from the store whenever the
    // project changes, so this only happens in the gap before it finishes; the
    // voice is dropped and the next pass over the same material plays.
    return null;
  }

  private startVoice(plan: VoicePlan): void {
    const ctx = this.ctx;
    const assets = this.assets;
    if (!ctx || !assets) return;

    const buffer = this.resolveBuffer(plan, ctx, assets);
    if (!buffer) return;

    const when = this.timeline.contextTimeFor(plan.startProjectSec);
    if (when === null) return;

    const graph = this.graphs.get(plan.trackId);
    if (!graph) return;

    const gain = ctx.createGain();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = plan.playbackRate;

    // The envelope gets a node of its own, ahead of the fades. It has to: fades
    // are written with `setValueCurveAtTime`, which owns the whole parameter for
    // its duration, so an envelope ramp scheduled underneath one would throw
    // rather than combine.
    const envelope = plan.envelope.length > 0 ? ctx.createGain() : null;
    if (envelope) {
      source.connect(envelope);
      envelope.connect(gain);
      applyVoiceEnvelope(envelope.gain, when, plan.envelope);
    } else {
      source.connect(gain);
    }
    gain.connect(plan.destination === 'preFader' ? graph.frozenInput : graph.input);

    this.applyVoiceGain(gain, when, plan);

    const sourceDuration = plan.durationSec * plan.playbackRate;
    const startAt = Math.max(when, ctx.currentTime);
    // A voice whose start has already passed begins immediately at a matching
    // offset rather than being skipped, so a late pump never leaves a hole.
    const lateBy = Math.max(0, ctx.currentTime - when);
    const offset = Math.max(0, plan.sourceOffsetSec + lateBy * plan.playbackRate);
    const duration = Math.max(0, sourceDuration - lateBy * plan.playbackRate);
    if (duration <= 0) {
      gain.disconnect();
      envelope?.disconnect();
      return;
    }

    try {
      source.start(startAt, offset, duration);
    } catch {
      gain.disconnect();
      envelope?.disconnect();
      return;
    }

    assets.pin(plan.assetId);
    const voice: Voice = {
      source,
      gain,
      envelope,
      assetId: plan.assetId,
      endsAtContextTime: startAt + duration / plan.playbackRate,
    };
    this.voices.add(voice);
    source.onended = () => this.releaseVoice(voice);
  }

  private applyVoiceGain(gain: GainNode, when: number, plan: VoicePlan): void {
    const base = plan.gain;
    gain.gain.cancelScheduledValues(when);
    gain.gain.setValueAtTime(base * (plan.fadeIn ? plan.fadeIn.startGain : 1), when);

    if (plan.fadeIn) this.applyFade(gain, when, base, plan.fadeIn, true);
    if (plan.fadeOut) this.applyFade(gain, when, base, plan.fadeOut, false);
  }

  /**
   * Fades are written as value curves rather than linear ramps so that an
   * equal-power fade actually sounds equal-power. 64 points is inaudibly fine
   * for fades of any musical length.
   */
  private applyFade(gain: GainNode, voiceStart: number, base: number, fade: VoiceFade, rising: boolean): void {
    if (fade.durationSec <= 0) {
      gain.gain.setValueAtTime(base * fade.endGain, voiceStart + fade.startOffsetSec);
      return;
    }
    const points = 64;
    const curve = new Float32Array(points);
    const span = rising ? 1 - fade.startGain : fade.startGain;
    for (let i = 0; i < points; i++) {
      const t = i / (points - 1);
      const shaped = fadeGainAt(fade.curve, t * fade.durationSec, fade.durationSec, rising, fade.curvature);
      // Re-map the curve into the remaining gain range for a partially-elapsed fade.
      const value = rising ? fade.startGain + shaped * span : shaped * fade.startGain;
      curve[i] = base * Math.min(1, Math.max(0, value));
    }
    try {
      gain.gain.setValueCurveAtTime(curve, voiceStart + fade.startOffsetSec, fade.durationSec);
    } catch {
      gain.gain.linearRampToValueAtTime(base * fade.endGain, voiceStart + fade.startOffsetSec + fade.durationSec);
    }
  }

  private scheduleMetronome(project: Project, windowStartSec: number, windowEndSec: number): void {
    const ctx = this.ctx;
    if (!ctx || !project.transport.metronomeEnabled || !this.clickBuffer || !this.metronomeGain) return;

    const map = tempoMapOf(project);
    const sigMap = signatureMapOf(project);
    const startTick = secondsToTicks(map, Math.max(windowStartSec, this.metronomeScheduledUntilSec));
    const endTick = secondsToTicks(map, windowEndSec);

    // beatGrid walks the meter map, so a signature change inside the look-ahead
    // window shortens the very next bar rather than being noticed a window late.
    for (const beat of beatGrid(sigMap, startTick, endTick, 512)) {
      const seconds = ticksToSeconds(map, beat.tick);
      const when = this.timeline.contextTimeFor(seconds);
      if (when === null || when < ctx.currentTime) continue;
      const source = ctx.createBufferSource();
      source.buffer = this.clickBuffer;
      // Downbeats an octave up, so the bar is audible without a second sample.
      source.playbackRate.value = beat.downbeat ? 2 : 1;
      source.connect(this.metronomeGain);
      source.start(when);
      source.onended = () => source.disconnect();
    }
    this.metronomeScheduledUntilSec = windowEndSec;
  }

  // -- voice lifecycle -----------------------------------------------------

  private releaseVoice(voice: Voice): void {
    if (!this.voices.has(voice)) return;
    this.voices.delete(voice);
    voice.source.onended = null;
    try {
      voice.source.disconnect();
    } catch {
      /* already disconnected */
    }
    voice.gain.disconnect();
    voice.envelope?.disconnect();
    this.assets?.unpin(voice.assetId);
  }

  private reapVoices(now: number): void {
    for (const voice of this.voices) {
      if (voice.endsAtContextTime + 1 < now) this.releaseVoice(voice);
    }
  }

  /**
   * Stops every sounding voice. A short fade avoids the click that an abrupt
   * `stop()` produces mid-waveform — the difference between a DAW that feels
   * finished and one that does not.
   */
  private stopVoices(immediate: boolean): void {
    const ctx = this.ctx;
    if (ctx) {
      // Scheduled notes live inside their instrument, not in `voices`, so they
      // have to be released explicitly or a held chord would ring on after stop.
      for (const graph of this.graphs.values()) graph.instrumentNode?.allNotesOff(ctx.currentTime);
    }
    for (const voice of [...this.voices]) {
      try {
        if (ctx && !immediate) {
          const now = ctx.currentTime;
          voice.gain.gain.cancelScheduledValues(now);
          voice.gain.gain.setValueAtTime(voice.gain.gain.value, now);
          voice.gain.gain.linearRampToValueAtTime(0, now + STOP_FADE_SEC);
          voice.source.stop(now + STOP_FADE_SEC);
        } else {
          voice.source.stop();
        }
      } catch {
        /* the source may already have ended */
      }
      if (immediate) this.releaseVoice(voice);
    }
  }

  // -- teardown ------------------------------------------------------------

  /** Releases graphs and voices but keeps the AudioContext for the next project. */
  unloadProject(): void {
    this.haltPlayback();
    this.setState('stopped');
    for (const graph of this.graphs.values()) graph.dispose();
    this.graphs.clear();
    this.connectedTo.clear();
    this.project = null;
    this.positionTicks = 0;
  }

  async dispose(): Promise<void> {
    this.preparing?.abort();
    this.preparing = null;
    this.preparedSignature = null;
    // Renders written in the last seconds of a session are worth as much as any
    // other, and after this the storage handle goes away.
    await this.stretches.flush();
    this.unloadProject();
    this.metronomeGain?.disconnect();
    this.metronomeGain = null;
    this.clickBuffer = null;
    this.assets?.dispose();
    this.assets = null;
    const ctx = this.ctx;
    this.ctx = null;
    if (ctx && ctx.state !== 'closed') await ctx.close();
  }
}

/** A short percussive click: a fast-decaying sine, band-limited so it never pops. */
function buildClick(ctx: AudioContext): AudioBuffer {
  const duration = 0.04;
  const length = Math.floor(ctx.sampleRate * duration);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    const t = i / ctx.sampleRate;
    const envelope = Math.exp(-t * 120) * Math.min(1, t * 4000);
    data[i] = Math.sin(2 * Math.PI * 1000 * t) * envelope * 0.8;
  }
  return buffer;
}
